import { Alert, Card, CardContent, CardHeader, Stack } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { Dispatch, FunctionComponent, SetStateAction, useMemo } from 'react';
import { QuestionConfig, QuestionId } from '../../types/GameplayTypes';
import { pluralize } from '../../util/Text';
import { FilterControls } from './FilterControls';
import { QuestionSelectionList, QuestionSetQuestionList } from './QuestionsList';

type Props = {
	questions: QuestionConfig[];
	selectionModel: GridRowSelectionModel;
	setSelectionModel: Dispatch<SetStateAction<GridRowSelectionModel>>;
	/** Array of QuestionIds in the desired display order for selected questions */
	orderedQuestionIds?: QuestionId[];
	/** Called when the order of selected questions changes. Receives array of QuestionIds in new order */
	onSelectionReorder?: (orderedIds: QuestionId[]) => void;
};

/**
 * Select questions for a QuestionSet. Based around two DataGrids, one for the selected questions, one
 * for the available.
 */
export const QuestionSelector: FunctionComponent<Props> = (props) => {
	const { questions, selectionModel, setSelectionModel, orderedQuestionIds } = props;

	const allTags = questions.flatMap(q => q.tags).uniqueValues().sort();
	const allAuthors = questions.map(q => q.authorName).uniqueValues().sort();

	const { selectedQuestions, unselectedQuestions } = useMemo(() => {
		const questionMap = new Map(questions.map(q => [q.id, q]));
		const selectedSet = selectionModel.ids;

		// Order selected questions according to orderedQuestionIds if provided
		let selected: QuestionConfig[];
		if (orderedQuestionIds && !orderedQuestionIds.empty()) {
			selected = orderedQuestionIds
				.filter(id => selectedSet.has(id))
				.map(id => questionMap.get(id))
				.filter(q => q !== undefined);
		} else {
			selected = questions.filter(q => selectedSet.has(q.id));
		}

		const unselected = questions.filter(q => !selectedSet.has(q.id));

		return { selectedQuestions: selected, unselectedQuestions: unselected };
	}, [questions, selectionModel, orderedQuestionIds]);

	const selectionCount = selectedQuestions.countWhere(q => q.isActive);

	return <>
		<Card>
			<CardHeader title={pluralize(selectionCount, 'round', { quantityPrefix: true })} />
			<CardContent>
				<Stack spacing={1}>
					<Stack direction='row'>
						<TooManyRoundsWarning activeSelectedCount={selectionCount} />
						<InactiveQuestionsWarning allSelectedCount={selectedQuestions.length} activeSelectedCount={selectionCount} />
					</Stack>
					<QuestionSetQuestionList
						questions={selectedQuestions}
						selectionModel={selectionModel}
						onSelectionChange={setSelectionModel}
						onReorder={props.onSelectionReorder}
					/>
				</Stack>
			</CardContent>
		</Card>
		<Card>
			<CardHeader title='Available questions' />
			<CardContent>
				<Stack spacing={2}>
					<FilterControls allTags={allTags} allAuthors={allAuthors} />
					<QuestionSelectionList
						questions={unselectedQuestions}
						selectionModel={selectionModel}
						onSelectionChange={setSelectionModel}
					/>
				</Stack>
			</CardContent>
		</Card>
	</>;
};

type WarningProps = {
	allSelectedCount: number;
	activeSelectedCount: number;
};

const InactiveQuestionsWarning: FunctionComponent<WarningProps> = (props) => {
	const inactiveCount = props.allSelectedCount - props.activeSelectedCount;
	return (inactiveCount > 0)
		&& <Alert severity='warning'>
			{`${pluralize(inactiveCount, 'inactive question', { quantityPrefix: true })}`}
		</Alert>;
};

const TooManyRoundsWarning: FunctionComponent<Pick<WarningProps, 'activeSelectedCount'>> = (props) => {
	const RECOMMENDED_ROUNDS_PER_WORKSHOP = 5;

	return (props.activeSelectedCount > RECOMMENDED_ROUNDS_PER_WORKSHOP) &&
		<Alert severity="info">
			For a one-hour workshop, we recommend {RECOMMENDED_ROUNDS_PER_WORKSHOP} rounds per Question Set.
		</Alert>;
};
